import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageIcon, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import {
  mediaAbsoluteUrl,
  mediaListQueryOptions,
  readImageDimensions,
  useUploadMedia,
} from '@/lib/media';

export interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with absolute URL + alt text after a successful upload or pick.
   * One of `onInsert` or `onPickMedia` must be provided. Editor-centric
   * callers (insert into prose) use this; cover-image callers use the
   * richer `onPickMedia`.
   */
  onInsert?: (url: string, alt: string) => void;
  /**
   * Sibling of onInsert that receives the full media meta (id + RELATIVE
   * url + alt). When provided, fires *instead of* onInsert. Used by
   * CoverImagePicker which persists the media id on the post row, not just
   * the URL.
   */
  onPickMedia?: (item: { id: string; url: string; altText: string | null }) => void;
  /**
   * 'insert' (default) = inserts a new image at the cursor.
   * 'replace' = swaps src+alt on the currently selected image node.
   * 'cover' = selecting a cover image (post / category). Affects copy only.
   */
  mode?: 'insert' | 'replace' | 'cover';
  /**
   * When true, this is rendered FROM the Media Library page — hide the
   * "เลือกจาก Library" tab (redundant) and use plain "อัปโหลด" button copy
   * instead of the editor-centric "อัปโหลด + ใส่ในบทความ".
   */
  libraryMode?: boolean;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';

type Tab = 'upload' | 'library';

export function ImageUploadDialog({
  open,
  onOpenChange,
  onInsert,
  onPickMedia,
  mode = 'insert',
  libraryMode = false,
}: ImageUploadDialogProps) {
  const isReplace = mode === 'replace';
  const isCover = mode === 'cover';
  const [tab, setTab] = useState<Tab>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [alt, setAlt] = useState('');
  const [librarySearch, setLibrarySearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadMedia();

  // Library tab data — only fetched when that tab is active + dialog is open.
  const libraryQuery = useQuery({
    ...mediaListQueryOptions({
      perPage: 24,
      kind: 'image',
      q: librarySearch || undefined,
    }),
    enabled: open && tab === 'library',
  });

  // Reset on open
  useEffect(() => {
    if (open) {
      setTab('upload');
      setFile(null);
      setAlt('');
      setPreviewUrl(null);
      setLibrarySearch('');
    }
  }, [open]);

  // Preview URL lifecycle for uploaded file
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    if (picked && picked.type.toLowerCase() === 'image/gif') {
      toast.error('GIF ไม่รองรับ — แปลงเป็น MP4 แทน');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file) return;
    const dims = await readImageDimensions(file);
    const media = await upload.mutateAsync({
      file,
      alt: alt.trim() || undefined,
      width: dims?.width,
      height: dims?.height,
    });
    if (onPickMedia) {
      onPickMedia({ id: media.id, url: media.url, altText: media.altText ?? (alt.trim() || null) });
    } else {
      onInsert?.(mediaAbsoluteUrl(media.url), media.altText ?? alt.trim());
    }
    onOpenChange(false);
  };

  const handlePickFromLibrary = (item: { id: string; url: string; altText: string | null }) => {
    if (onPickMedia) {
      onPickMedia(item);
    } else {
      onInsert?.(mediaAbsoluteUrl(item.url), item.altText ?? '');
    }
    onOpenChange(false);
  };

  const libraryItems = libraryQuery.data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isReplace
              ? 'แทนรูปภาพ'
              : isCover
                ? 'เลือกรูปปก'
                : libraryMode
                  ? 'อัปโหลดสื่อใหม่'
                  : 'เพิ่มรูปภาพ'}
          </DialogTitle>
          <DialogDescription>
            {isReplace
              ? 'เลือกรูปใหม่เพื่อแทนที่รูปปัจจุบัน (ตำแหน่งและ caption คงเดิม)'
              : libraryMode
                ? 'อัปโหลดไฟล์ใหม่เข้า Media Library (JPEG / PNG / WebP / AVIF · สูงสุด 25 MB)'
                : 'อัปโหลดไฟล์ใหม่ หรือเลือกจากที่อัปโหลดไว้แล้ว (JPEG / PNG / WebP / AVIF · สูงสุด 25 MB)'}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs — hidden in libraryMode (we ARE the library; the picker tab
            would be redundant) */}
        {!libraryMode && (
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-800 dark:bg-slate-900">
            <button
              type="button"
              onClick={() => setTab('upload')}
              className={
                tab === 'upload'
                  ? 'bg-brand-600 flex-1 rounded px-3 py-1.5 text-xs font-medium text-white'
                  : 'flex-1 rounded px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-800'
              }
            >
              อัปโหลดใหม่
            </button>
            <button
              type="button"
              onClick={() => setTab('library')}
              className={
                tab === 'library'
                  ? 'bg-brand-600 flex-1 rounded px-3 py-1.5 text-xs font-medium text-white'
                  : 'flex-1 rounded px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-800'
              }
            >
              เลือกจาก Media Library
            </button>
          </div>
        )}

        {tab === 'upload' && (
          <>
            <div className="my-4 space-y-3">
              <div>
                <Label htmlFor="image-upload-file" className="mb-1.5 block">
                  ไฟล์
                </Label>
                {previewUrl ? (
                  <div className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                    <img src={previewUrl} alt="" className="max-h-64 w-full object-contain" />
                    <button
                      type="button"
                      className="absolute right-2 top-2 rounded bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      เลือกใหม่
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload-file"
                    className="hover:border-brand-500 hover:bg-brand-50 dark:hover:border-brand-400 flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <ImageIcon className="h-6 w-6" />
                    <span className="text-sm">คลิกเพื่อเลือกไฟล์ภาพ</span>
                  </label>
                )}
                <input
                  ref={fileInputRef}
                  id="image-upload-file"
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={handleSelect}
                />
                {file && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB · {file.type}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="image-upload-alt" className="mb-1.5 block">
                  คำอธิบายภาพ (alt text)
                </Label>
                <Input
                  id="image-upload-alt"
                  type="text"
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  placeholder="ใส่คำอธิบายเพื่อ accessibility + SEO"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={upload.isPending}
              >
                ยกเลิก
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void handleUpload();
                }}
                disabled={!file || upload.isPending}
              >
                <Upload className="mr-2 h-4 w-4" />
                {upload.isPending
                  ? 'กำลังอัปโหลด...'
                  : isReplace
                    ? 'อัปโหลด + แทนรูป'
                    : isCover
                      ? 'อัปโหลด + ใช้เป็นรูปปก'
                      : libraryMode
                        ? 'อัปโหลด'
                        : 'อัปโหลด + ใส่ในบทความ'}
              </Button>
            </DialogFooter>
          </>
        )}

        {tab === 'library' && (
          <div className="my-4 space-y-3">
            <Input
              type="text"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="ค้นหาตามชื่อไฟล์ / alt text..."
            />

            {libraryQuery.isLoading ? (
              <p className="py-8 text-center text-sm text-slate-500">กำลังโหลด...</p>
            ) : libraryItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">
                ไม่พบรูปใน library — ลองอัปโหลดใหม่
              </p>
            ) : (
              <div className="grid max-h-[60vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4 md:grid-cols-5">
                {libraryItems.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() =>
                      handlePickFromLibrary({ id: m.id, url: m.url, altText: m.altText })
                    }
                    className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100 hover:border-slate-400 dark:border-slate-800 dark:bg-slate-800"
                    title={m.originalName}
                  >
                    <img
                      src={mediaAbsoluteUrl(m.url)}
                      alt={m.altText ?? m.originalName}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
